import { useEffect, useRef } from 'react';
import { DOT, dotLook, type DotFieldOptions } from '@/lib/studioB/dotField';

const SPACING = 24;
const FRAME_MS = 33;

/** The --b-dot token ("262 30% 10% / 0.16") as a colour without its alpha, the field's alpha, and the violet a ring lights. */
function colours(): { ink: string; alpha: number; violet: string } {
  const css = getComputedStyle(document.documentElement);
  const [ink, alpha] = css.getPropertyValue('--b-dot').trim().split('/').map((s) => s.trim());
  return { ink: ink || '262 30% 10%', alpha: Number(alpha) || 0.16, violet: css.getPropertyValue('--primary').trim() || '262.1 83.3% 57.8%' };
}

/**
 * Direction B (beta only): centre stage's dot field. At rest it is prod home's, still; while Ivy works violet rings
 * ripple out from where she is, and the reading column stays clear. One canvas behind everything: it animates only
 * while she works, at most 30 frames a second, paused while the tab is hidden; with reduced motion it never animates.
 */
export function LivingDots({ working = false, column, band, origin }: {
  working?: boolean;
  /** Width of the centred reading column to keep clear (px), or none. */
  column?: number;
  /** Fade the field out between these heights (px from the top). */
  band?: { from: number; to: number };
  /** Where Ivy is working, for the ripple: read at each frame. */
  origin?: () => { x: number; y: number } | null;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const live = useRef({ working, column, band, origin });
  live.current = { working, column, band, origin };
  const wake = useRef<() => void>(() => {});

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const t0 = performance.now();
    let raf = 0;
    let last = 0;
    let w = 0;
    let h = 0;

    const size = () => {
      const box = canvas.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      w = box.width;
      h = box.height;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = (now: number) => {
      const { working: busy, column: col, band: b, origin: where } = live.current;
      const { ink, alpha, violet } = colours();
      const box = canvas.getBoundingClientRect();
      const at = where?.();
      const opts: DotFieldOptions = {
        spacing: SPACING,
        alpha,
        motion: !still,
        working: busy,
        origin: at ? { x: at.x - box.left, y: at.y - box.top } : { x: w / 2, y: h * 0.75 },
        column: col ? { left: (w - col) / 2, right: (w + col) / 2, fade: 90 } : null,
        band: b ?? null,
      };
      const t = (now - t0) / 1000;
      ctx.clearRect(0, 0, w, h);
      for (let y = SPACING / 2; y < h; y += SPACING) {
        for (let x = SPACING / 2; x < w; x += SPACING) {
          const dot = dotLook(x, y, t, opts);
          if (dot.a <= 0) continue;
          ctx.fillStyle = `hsl(${ink} / ${dot.a.toFixed(3)})`;
          ctx.beginPath();
          ctx.arc(x, y, DOT, 0, Math.PI * 2);
          ctx.fill();
          if (dot.lit < 0.02) continue;
          ctx.fillStyle = `hsl(${violet} / ${(0.2 + 0.65 * dot.lit).toFixed(3)})`;
          ctx.beginPath();
          ctx.arc(x, y, DOT + 1.6 * dot.lit, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    };

    const loop = (now: number) => {
      raf = 0;
      if (now - last >= FRAME_MS) {
        last = now;
        draw(now);
      }
      if (live.current.working && !document.hidden) raf = requestAnimationFrame(loop);
      else draw(now);
    };

    wake.current = () => {
      if (still || raf || document.hidden || !live.current.working) {
        if (!raf) draw(performance.now());
        return;
      }
      raf = requestAnimationFrame(loop);
    };

    size();
    wake.current();
    const resized = new ResizeObserver(() => {
      size();
      draw(performance.now());
    });
    resized.observe(canvas);
    const onVisible = () => wake.current();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelAnimationFrame(raf);
      wake.current = () => {};
      resized.disconnect();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  useEffect(() => wake.current(), [working]);

  return <canvas ref={ref} aria-hidden="true" data-living-dots className="pointer-events-none absolute inset-0 -z-10 h-full w-full" />;
}
